import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import {NatResidentParkingUsesLocationState} from './NatResidentApplicationInterface'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {NatPutParkingUsesRequest, NatPutParkingUsesResponse} from '../repository/NatPutParkingUses'
import NatResidentParkingUsesForm from './resident/NatResidentParkingUsesForm'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {NatGetParkingUsesResponse} from '../repository/NatGetParkingUses'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentParkingUsesLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    useArea: Nullable<string>
    useStartDate: Nullable<string>
    userFamilyName: Nullable<string>
    userFirstName: Nullable<string>
    userAttribute: Nullable<string>
    ownerFamilyName: Nullable<string>
    ownerFirstName: Nullable<string>
    carBodyShape: Nullable<string>
    carName: Nullable<string>
    carRegistrationNumber: Nullable<string>
    engineSize: Nullable<string>
    length: Nullable<string>
    height: Nullable<string>
    width: Nullable<string>
    vehicleWeight: Nullable<string>
    vehicleTotalWeight: Nullable<string>
    isLoadCompleted: boolean
}

class NatResidentParkingUsesInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            useArea: null,
            useStartDate: null,
            userFamilyName: null,
            userFirstName: null,
            userAttribute: null,
            ownerFamilyName: null,
            ownerFirstName: null,
            carBodyShape: null,
            carName: null,
            carRegistrationNumber: null,
            engineSize: null,
            length: null,
            height: null,
            width: null,
            vehicleWeight: null,
            vehicleTotalWeight: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {condominiumCd, applicationRepo, eventEmitter} = this.props
        const {applyInfo, applyId} = this.props.location.state

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultUseArea = applyInfo?.useArea ?? null
        let defaultUseStartDate = applyInfo?.useStartDate ?? null
        let defaultUserFamilyName = applyInfo?.userFamilyName ?? null
        let defaultUserFirstName = applyInfo?.userFirstName ?? null
        let defaultUserAttribute = applyInfo?.userAttribute ?? null
        let defaultOwnerFamilyName = applyInfo?.ownerFamilyName ?? null
        let defaultOwnerFirstName = applyInfo?.ownerFirstName ?? null
        let defaultCarBodyShape = applyInfo?.carBodyShape ?? null
        let defaultCarName = applyInfo?.carName ?? null
        let defaultCarRegistrationNumber = applyInfo?.carRegistrationNumber ?? null
        let defaultEngineSize = applyInfo?.engineSize ?? null
        let defaultLength = applyInfo?.length ?? null
        let defaultHeight = applyInfo?.height ?? null
        let defaultWidth = applyInfo?.width ?? null
        let defaultVehicleWeight = applyInfo?.vehicleWeight ?? null
        let defaultVehicleTotalWeight = applyInfo?.vehicleTotalWeight ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const parkingUsesDetail: Nullable<NatGetParkingUsesResponse> = await this.props.asyncCallFunction(
                    applicationRepo.getParkingUsesForResident.bind(applicationRepo, condominiumCd, applyId),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (parkingUsesDetail && parkingUsesDetail.details) {
                    defaultUseArea = parkingUsesDetail.details.useArea
                    defaultUseStartDate = parkingUsesDetail.details.useStartDate
                    defaultUserFamilyName = parkingUsesDetail.details.userFamilyName
                    defaultUserFirstName = parkingUsesDetail.details.userFirstName
                    defaultUserAttribute = parkingUsesDetail.details.userAttribute
                    defaultOwnerFamilyName = parkingUsesDetail.details.ownerFamilyName
                    defaultOwnerFirstName = parkingUsesDetail.details.ownerFirstName
                    defaultCarBodyShape = parkingUsesDetail.details.carBodyShape
                    defaultCarName = parkingUsesDetail.details.carName
                    defaultCarRegistrationNumber = parkingUsesDetail.details.carRegistrationNumber
                    defaultEngineSize = parkingUsesDetail.details.engineSize
                    defaultLength = parkingUsesDetail.details.length
                    defaultHeight = parkingUsesDetail.details.height
                    defaultWidth = parkingUsesDetail.details.width
                    defaultVehicleWeight = parkingUsesDetail.details.vehicleWeight
                    defaultVehicleTotalWeight = parkingUsesDetail.details.vehicleTotalWeight
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            useArea: defaultUseArea,
            useStartDate: defaultUseStartDate,
            userFamilyName: defaultUserFamilyName,
            userFirstName: defaultUserFirstName,
            userAttribute: defaultUserAttribute,
            ownerFamilyName: defaultOwnerFamilyName,
            ownerFirstName: defaultOwnerFirstName,
            carBodyShape: defaultCarBodyShape,
            carName: defaultCarName,
            carRegistrationNumber: defaultCarRegistrationNumber,
            engineSize: defaultEngineSize,
            length: defaultLength,
            height: defaultHeight,
            width: defaultWidth,
            vehicleWeight: defaultVehicleWeight,
            vehicleTotalWeight: defaultVehicleTotalWeight,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutParkingUsesRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutParkingUsesResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyParkingUses.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const locationState: NatResidentParkingUsesLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        useArea: request.useArea,
                        useStartDate: request.useStartDate,
                        userFamilyName: request.userFamilyName,
                        userFirstName: request.userFirstName,
                        userAttribute: request.userAttribute,
                        ownerFamilyName: request.ownerFamilyName,
                        ownerFirstName: request.ownerFirstName,
                        carBodyShape: request.carBodyShape,
                        carName: request.carName,
                        carRegistrationNumber: request.carRegistrationNumber,
                        engineSize: request.engineSize,
                        length: request.length,
                        height: request.height,
                        width: request.width,
                        vehicleWeight: request.vehicleWeight,
                        vehicleTotalWeight: request.vehicleTotalWeight,
                    },
                    applyId: this.props.location.state.applyId,
                }
                this.props.navigate(`/resident/condominiums/${this.props.condominiumCd}/applications/parkingUses/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.location.state.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/parkingUses/${this.props.location.state.applyId}/copy`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications/parkingUses/agreement`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    駐車場使用申込
                </PageTitle>
                <Box height={'30px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentParkingUsesForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentParkingUsesInputPage)
